import {Inject, Injectable} from '@angular/core';
import {ProductionOrder} from '@domain/planning-prototype/carpet/production-order';
import {ProductionOrderCustomSettings} from '@domain/production-schedule/custom-settings/production-order-custom-settings';
import {FinishingForProductionSchedule} from '@domain/production-schedule/finishing-for-production-schedule';
import {ProductionSchedule} from '@domain/production-schedule/production-schedule';
import {FINISHINGS, Finishings} from '@infrastructure/http/finishing/finishings';
import {HttpProductionOrdersCustomSettingsService} from '@infrastructure/http/production-order-custom-settings/http-production-orders-custom-settings.service';
import {PRODUCTION_SCHEDULES, ProductionSchedules} from '@infrastructure/http/production-schedule/production-schedules';
import {OverviewListFinishing} from '@presentation/pages/textile-data/finishing/overview/overview-list-finishing';
import {AssertionUtils, DialogBuilderFactoryService, Unit} from '@vdw/angular-component-library';
import {map, Observable, of, switchMap, tap} from 'rxjs';
import {ConfirmProductionScheduleOnDesktopInput} from '../../production-schedule/confirm/confirm-desktop/confirm-production-schedule-desktop-input.interface';
import {ConfirmProductionScheduleDesktopComponent} from '../../production-schedule/confirm/confirm-desktop/confirm-production-schedule-desktop.component';

@Injectable()
export class ProductionOrderUtilities {
  public productionOrderCustomSettings: ProductionOrderCustomSettings;

  public constructor(
    @Inject(PRODUCTION_SCHEDULES) private readonly productionSchedules: ProductionSchedules,
    @Inject(FINISHINGS) private readonly finishings: Finishings,
    private readonly httpProductionOrderCustomSettings: HttpProductionOrdersCustomSettingsService,
    private readonly dialogBuilder: DialogBuilderFactoryService
  ) {}

  public reconfirmOrder(order: ProductionOrder): Observable<boolean> {
    return this.loadListOfProductionOrderCustomSettings().pipe(
      switchMap(() => this.productionSchedules.getById(order.id)),
      switchMap((schedule: ProductionSchedule) => this.updateFinishing(schedule)),
      switchMap((schedule: ProductionSchedule) => this.openReconfirmDialog(schedule))
    );
  }

  private openReconfirmDialog(schedule: ProductionSchedule): Observable<boolean> {
    return this.dialogBuilder
      .getBuilder()
      .withClass('confirm-production-schedule-dialog')
      .withCloseDisabled()
      .openDialog(ConfirmProductionScheduleDesktopComponent, {
        productionSchedule: schedule,
        commercialUnit: Unit.CENTIMETER,
        productionOrderCustomSettings: this.productionOrderCustomSettings
      } as ConfirmProductionScheduleOnDesktopInput)
      .pipe(map((result: ProductionSchedule) => !AssertionUtils.isNullOrUndefined(result)));
  }

  private updateFinishing(schedule: ProductionSchedule): Observable<ProductionSchedule> {
    if (AssertionUtils.isNullOrUndefined(schedule.finishing)) {
      return of(schedule);
    }
    return this.finishings.getOverviewListFinishingById(schedule.finishing.id).pipe(
      tap((finishing: OverviewListFinishing) => schedule.updateFinishing(FinishingForProductionSchedule.fromOverviewListFinishing(finishing))),
      map(() => schedule)
    );
  }

  private loadListOfProductionOrderCustomSettings(): Observable<void> {
    if (!AssertionUtils.isNullOrUndefined(this.productionOrderCustomSettings)) {
      return of(null);
    }
    return this.httpProductionOrderCustomSettings.get().pipe(
      tap((customSettings: ProductionOrderCustomSettings) => (this.productionOrderCustomSettings = customSettings)),
      map(() => null)
    );
  }
}
